import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ChartPieSlice, Robot, CaretDown, MagnifyingGlass, Check, Coins, ChartBar, Flask, Trophy } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { AdminShell } from "@/components/AdminShell";

const fmt = (v) => (v == null ? "" : v < 0.01 && v > 0 ? `$${v}` : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

const ModelPicker = ({ data, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("All");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  if (!data) return null;
  const sel = data.models.find((m) => m.id === selected) || data.models[0];
  const list = data.models.filter((m) =>
    (brand === "All" || m.brand === brand || m.id === "auto") &&
    (!q || m.name.toLowerCase().includes(q.toLowerCase()) || m.id.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="relative" ref={ref}>
      <button data-testid="profit-model-picker-btn" onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-gray-300 bg-white rounded-lg px-3.5 py-2 text-sm font-semibold text-gray-800 hover:border-gray-400 transition-colors">
        <Robot size={15} className="text-blue-600" /> {sel?.name || "Auto (recommended)"} <CaretDown size={12} />
      </button>
      {open && (
        <div data-testid="model-picker-dropdown" className="absolute right-0 mt-2 w-[560px] max-w-[90vw] bg-white border border-gray-200 rounded-xl shadow-2xl z-50 p-3">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 mb-2">
            <MagnifyingGlass size={14} className="text-gray-400" />
            <input data-testid="model-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search models…" className="flex-1 text-sm outline-none" autoFocus />
          </div>
          <div className="flex gap-2 max-h-[380px]">
            <div data-testid="brand-rail" className="w-36 shrink-0 overflow-y-auto space-y-0.5 pr-1 border-r border-gray-100">
              <button data-testid="brand-filter-all" onClick={() => setBrand("All")}
                className={`w-full text-left text-[13px] rounded-md px-2.5 py-1.5 font-semibold ${brand === "All" ? "bg-blue-700 text-white" : "hover:bg-gray-100 text-gray-700"}`}>
                All ({data.total})
              </button>
              {data.brands.map((b) => (
                <button key={b.name} data-testid={`brand-filter-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setBrand(b.name)}
                  className={`w-full text-left text-[13px] rounded-md px-2.5 py-1.5 ${brand === b.name ? "bg-blue-700 text-white font-semibold" : "hover:bg-gray-100 text-gray-600"}`}>
                  {b.name} ({b.count})
                </button>
              ))}
            </div>
            <div data-testid="model-list" className="flex-1 overflow-y-auto space-y-0.5">
              {list.slice(0, 250).map((m) => (
                <button key={m.id} data-testid={`model-row-${m.id.replace(/[^a-z0-9]+/gi, "-")}`}
                  onClick={() => { onSelect(m); setOpen(false); }}
                  className={`w-full text-left rounded-lg px-3 py-2 hover:bg-gray-50 ${m.id === selected ? "bg-blue-50" : ""}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    {m.name}
                    {m.id === "auto" && <span className="text-[9px] font-bold border border-gray-300 rounded px-1.5 py-0.5 tracking-widest text-gray-500">SMART ROUTING</span>}
                    {m.id !== "auto" && <span className="text-[9px] font-bold border border-gray-200 rounded px-1 py-0.5 text-gray-400">API</span>}
                    {m.id === selected && <Check size={14} className="ml-auto text-blue-600" />}
                  </span>
                  <span className="text-xs text-gray-500">
                    {m.id === "auto" ? "Picks the best model for each question" :
                      `${m.brand} · ${fmt(m.price_in)} in · ${fmt(m.price_out)} out /1M`}
                  </span>
                </button>
              ))}
              {list.length === 0 && <p data-testid="model-list-empty" className="text-sm text-gray-400 px-3 py-6 text-center">No models match your search</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TABS = [
  ["costs", "Costs", Coins], ["dashboard", "Profit Dashboard", ChartBar],
  ["simulator", "Simulator", Flask], ["competitors", "Competitors", Trophy],
  ["advisor", "AI Advisor", Robot],
];
const CompetitorsTab = ({ onAnalyze }) => {
  const [list, setList] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ name: "", price_usd: "", notes: "" });
  const load = () => api.get("/admin/profit/competitors").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/packages").then((r) => setPkgs(r.data.filter((p) => p.price_monthly > 0))).catch(() => {}); }, []);
  const add = async () => {
    try { await api.post("/admin/profit/competitors", { ...f, price_usd: parseFloat(f.price_usd) }); setF({ name: "", price_usd: "", notes: "" }); toast.success("Competitor added"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const rows = [
    ...list.map((c) => ({ ...c, you: false })),
    ...pkgs.map((p) => ({ id: `you-${p.name}`, name: p.name, price_usd: p.price_monthly, period: "month", notes: "Your package", you: true })),
  ].sort((a, b) => a.price_usd - b.price_usd);
  return (
    <div data-testid="competitors-tab" className="space-y-4">
      <div data-testid="comp-analyze-card" className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-blue-900">Want a verdict? The AI Advisor compares your packages against this list using your real financials.</p>
        <button data-testid="comp-analyze-btn"
          onClick={() => onAnalyze("Analyze my pricing vs my competitors. Where do my packages sit in the market, which price change would you test first, and what monthly profit impact do you expect?")}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 shrink-0">🤖 Analyze my pricing vs competitors</button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input data-testid="comp-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Competitor (e.g. Superhuman)" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-52" />
        <input data-testid="comp-price-input" type="number" min="0" value={f.price_usd} onChange={(e) => setF({ ...f, price_usd: e.target.value })} placeholder="$/mo" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-24" />
        <input data-testid="comp-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-56" />
        <button data-testid="comp-add-btn" onClick={add} disabled={!f.name.trim() || f.price_usd === ""} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-50">+ Add</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="competitors-table">
          <thead className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase"><tr><th className="px-5 py-2">Competitor</th><th className="px-3 py-2">Price</th><th className="px-3 py-2">Notes</th><th className="w-16"></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className={`border-t border-gray-100 ${c.you ? "bg-blue-50/60" : ""}`} data-testid={c.you ? `comp-you-row-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : `comp-row-${c.id}`}>
                <td className="px-5 py-2.5 font-medium">
                  {c.name}
                  {c.you && <span data-testid={`you-are-here-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="ml-2 text-[10px] font-bold bg-blue-600 text-white rounded-full px-2 py-0.5">◀ YOU ARE HERE</span>}
                </td>
                <td className="px-3 py-2.5">{usd(c.price_usd)}/{c.period}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{c.notes}</td>
                <td className="px-3 py-2.5">{!c.you && <button data-testid={`comp-del-${c.id}`} onClick={async () => { await api.delete(`/admin/profit/competitors/${c.id}`); load(); }} className="text-xs font-semibold text-red-600">Delete</button>}</td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-center text-sm text-gray-400" data-testid="comp-empty">No competitor entries yet — the highlighted rows are your own packages; add the rivals you're up against</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">The AI Advisor tab sees this list plus your monthly financials — the button above jumps straight there.</p>
    </div>
  );
};

const AdvisorTab = ({ externalPrompt, onConsumed, modelLabel }) => {
  const [sid] = useState(() => `adv-${Date.now()}`);
  const [msg, setMsg] = useState("");
  const [chat, setChat] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSnap, setShowSnap] = useState(false);
  const [snap, setSnap] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const onAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sid);
      fd.append("file", file);
      const { data } = await api.post("/admin/profit/advisor/attach", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data.filename]);
      toast.success(`${data.filename} attached — the advisor can now use it`);
    } catch (er) { toast.error(errDetail(er)); }
    finally { setAttaching(false); e.target.value = ""; }
  };
  const ask = async (text) => {
    const m = (text || msg).trim();
    if (!m || busy) return;
    setBusy(true); setMsg("");
    setChat((c) => [...c, { role: "user", content: m }]);
    try {
      const { data } = await api.post("/admin/profit/advisor", { session_id: sid, message: m });
      setChat((c) => [...c, { role: "assistant", content: data.answer, model: data.model }]);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (externalPrompt) { ask(externalPrompt); onConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPrompt]);
  const preview = async () => {
    if (!showSnap && !snap) {
      try { const { data } = await api.get("/admin/profit/advisor/snapshot-preview"); setSnap(data); } catch (e) { toast.error(errDetail(e)); return; }
    }
    setShowSnap(!showSnap);
  };
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [chat, busy]);
  const SUGGESTIONS = ["What's my break-even at 100 users?", "How does my pricing compare to my competitors?", "Which cost should I cut first to raise my margin?"];
  return (
    <div data-testid="advisor-tab" className="flex flex-col bg-gray-50 border border-gray-200 rounded-lg" style={{ height: "calc(100vh - 310px)", minHeight: 440 }}>
      <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
        <p className="text-sm font-bold text-gray-900 flex items-center gap-2"><Robot size={16} className="text-blue-600" /> AI Advisor <span className="text-xs font-normal text-gray-400">· answering with {modelLabel}</span></p>
        <p data-testid="advisor-model-info" className="text-[11px] text-gray-400 mt-0.5">
          It sees your full aggregated economics — costs, profit dashboard, live simulator baseline, competitors and distributor terms — never raw personal data.
          "Auto" = OpenRouter smart routing: each question is analyzed and routed to the best-fit model; the model that actually answered is shown under every reply.
        </p>
      </div>
      <div data-testid="advisor-chat" className="flex-1 overflow-y-auto p-4 space-y-2">
        {chat.length === 0 && !busy && (
          <div className="text-center pt-10 space-y-3" data-testid="advisor-empty">
            <p className="text-sm text-gray-400">Ask anything about your pricing, costs and profit.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`advisor-suggest-${i}`} onClick={() => ask(s)}
                  className="text-xs bg-white border border-gray-300 rounded-full px-3 py-1.5 hover:border-blue-400 text-gray-600">{s}</button>
              ))}
            </div>
          </div>
        )}
        {chat.map((c, i) => (
          <div key={i} data-testid={`advisor-msg-${c.role}-${i}`} className={`text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${c.role === "user" ? "bg-blue-600 text-white ml-16" : "bg-white border border-gray-200 mr-8"}`}>
            {c.content}
            {c.role === "assistant" && c.model && <span data-testid={`advisor-msg-model-${i}`} className="block mt-1 text-[10px] text-gray-400">{c.model}</span>}
          </div>
        ))}
        {busy && <p data-testid="advisor-thinking" className="text-xs text-gray-400 px-2">Thinking…</p>}
        <div ref={endRef} />
      </div>
      {showSnap && snap && <pre data-testid="advisor-snapshot-preview" className="text-[10px] bg-white border-t border-gray-200 p-2 max-h-40 overflow-auto shrink-0">{JSON.stringify(snap, null, 1)}</pre>}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pt-2" data-testid="advisor-attachments">
          {attachments.map((f, i) => (
            <span key={i} data-testid={`advisor-attachment-chip-${i}`} className="text-[11px] bg-blue-100 text-blue-800 rounded-full px-2.5 py-1 font-semibold">⌸ {f} ✓</span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 p-3 border-t border-gray-200 bg-white rounded-b-lg">
        <input ref={fileRef} data-testid="advisor-attach-input" type="file" accept=".zip" className="hidden" onChange={onAttach} />
        <button data-testid="advisor-attach-btn" onClick={() => fileRef.current?.click()} disabled={attaching}
          title="Attach an Export DB ZIP as extra context for this session"
          className="text-xs text-gray-500 border border-gray-300 rounded-md px-2.5 py-2 hover:bg-gray-50 disabled:opacity-50 shrink-0">
          {attaching ? "Uploading…" : "⌸ attach"}
        </button>
        <input data-testid="advisor-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && ask()} disabled={busy}
          placeholder='Ask the AI Advisor — e.g. "What price makes me profitable at 100 users?"' className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm outline-none" />
        <button data-testid="advisor-preview-btn" onClick={preview} title="Preview the data shared with the AI" className="text-xs text-gray-500 border border-gray-300 rounded-md px-2.5 py-2 hover:bg-gray-50">👁 data</button>
        <button data-testid="advisor-ask-btn" onClick={() => ask()} disabled={busy || !msg.trim()} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">Ask →</button>
      </div>
    </div>
  );
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SimulatorTab = () => {
  const [f, setF] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [live, setLive] = useState(null);
  const [res, setRes] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [name, setName] = useState("");
  const [compare, setCompare] = useState(null);

  const applyBaseline = (b) => {
    setLive({ ...b.live, month: b.month });
    setPkgs(b.packages.map((p) => ({ name: p.name, price: p.price, mix: p.mix_pct })));
    setBoosts(b.boosters.map((x) => ({ name: x.name, price: x.price, buys: x.buys_per_user })));
    setF({ users: b.users, referred: b.referred_share_pct, comm: b.commission_pct,
      window: b.commission_window_months || "", fixed: b.fixed_costs, variable: b.variable_costs,
      growth: 0, token_cpu: b.token_cost_per_user, fee_pct: b.payment_fee_pct });
  };
  const loadBaseline = () => api.get("/admin/profit/simulator/baseline").then((r) => applyBaseline(r.data)).catch((e) => toast.error(errDetail(e)));
  useEffect(() => { loadBaseline(); }, []);

  const body = (fv, pv, bv) => ({
    package_prices: Object.fromEntries(pv.map((p) => [p.name, +p.price || 0])),
    plan_mix: Object.fromEntries(pv.map((p) => [p.name, +p.mix || 0])),
    users: +fv.users || 0,
    boosters: bv.map((x) => ({ name: x.name, price: +x.price || 0, buys_per_user: +x.buys || 0 })),
    referred_share_pct: +fv.referred || 0, commission_pct: +fv.comm || 0,
    commission_window_months: fv.window ? +fv.window : null,
    fixed_costs: +fv.fixed || 0, variable_costs: +fv.variable || 0, variable_growth_pct: +fv.growth || 0,
    token_cost_per_user: +fv.token_cpu || 0, payment_fee_pct: +fv.fee_pct || 0,
  });

  useEffect(() => {
    if (!f) return;
    const t = setTimeout(() => {
      api.post("/admin/profit/simulate", body(f, pkgs, boosts)).then((r) => setRes(r.data)).catch((e) => toast.error(errDetail(e)));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, pkgs, boosts]);
  const loadScen = () => api.get("/admin/profit/scenarios").then((r) => setScenarios(r.data)).catch(() => {});
  useEffect(() => { loadScen(); }, []);

  const save = async () => {
    try { await api.post("/admin/profit/scenarios", { name, inputs: { f, pkgs, boosts } }); setName(""); toast.success("Scenario saved"); loadScen(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const loadScenario = (s) => {
    if (!s.inputs?.f) return toast.error("Old scenario format — please re-save it");
    setF(s.inputs.f); setPkgs(s.inputs.pkgs || []); setBoosts(s.inputs.boosts || []);
  };
  const applyCompare = async (s) => {
    if (!s) { setCompare(null); return; }
    if (!s.inputs?.f) return toast.error("Old scenario format — please re-save it");
    try { const { data } = await api.post("/admin/profit/simulate", body(s.inputs.f, s.inputs.pkgs || [], s.inputs.boosts || [])); setCompare({ name: s.name, curve: data.curve }); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const N = (k, label, step = 1, w = "w-20") => (
    <label className="text-xs text-gray-600">{label}
      <input data-testid={`sim-${k}`} type="number" step={step} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className={`block border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-0.5 ${w}`} />
    </label>
  );
  if (!f) return <p className="text-sm text-gray-400 p-6" data-testid="sim-loading">Loading your live data…</p>;
  const curveData = res ? res.curve.map((p) => {
    const cmp = compare?.curve?.find((q) => q.users === p.users);
    return { ...p, compare_profit: cmp ? cmp.profit : undefined };
  }) : [];
  return (
    <div data-testid="simulator-tab" className="grid lg:grid-cols-[420px_1fr] gap-4 items-start">
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold text-gray-400 uppercase">Prefilled from your real data</p>
          <button data-testid="sim-reset-live" onClick={loadBaseline}
            className="text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-md px-2.5 py-1 hover:bg-blue-100">↺ Reset to live data</button>
        </div>
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-1.5">Your packages — live prices & real plan mix</p>
          <div className="space-y-1.5">
            {pkgs.map((p, i) => (
              <div key={p.name} className="flex items-center gap-2" data-testid={`sim-pkg-row-${slug(p.name)}`}>
                <span className="text-xs font-semibold text-gray-700 w-24 truncate">{p.name}</span>
                <label className="text-[10px] text-gray-400">$
                  <input data-testid={`sim-pkg-price-${slug(p.name)}`} type="number" step="1" value={p.price}
                    onChange={(e) => setPkgs(pkgs.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                    className="block border border-gray-300 rounded-md px-2 py-1 text-sm w-20" />
                </label>
                <label className="text-[10px] text-gray-400">mix %
                  <input data-testid={`sim-pkg-mix-${slug(p.name)}`} type="number" step="1" value={p.mix}
                    onChange={(e) => setPkgs(pkgs.map((x, j) => j === i ? { ...x, mix: e.target.value } : x))}
                    className="block border border-gray-300 rounded-md px-2 py-1 text-sm w-16" />
                </label>
              </div>
            ))}
          </div>
        </div>
        {boosts.length > 0 && (
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1.5">Your boosters — live</p>
            <div className="space-y-1.5">
              {boosts.map((x, i) => (
                <div key={x.name} className="flex items-center gap-2" data-testid={`sim-boost-row-${slug(x.name)}`}>
                  <span className="text-xs font-semibold text-gray-700 w-24 truncate">{x.name}</span>
                  <label className="text-[10px] text-gray-400">$
                    <input data-testid={`sim-boost-price-${slug(x.name)}`} type="number" step="1" value={x.price}
                      onChange={(e) => setBoosts(boosts.map((b, j) => j === i ? { ...b, price: e.target.value } : b))}
                      className="block border border-gray-300 rounded-md px-2 py-1 text-sm w-20" />
                  </label>
                  <label className="text-[10px] text-gray-400">buys/user/mo
                    <input data-testid={`sim-boost-buys-${slug(x.name)}`} type="number" step="0.1" value={x.buys}
                      onChange={(e) => setBoosts(boosts.map((b, j) => j === i ? { ...b, buys: e.target.value } : b))}
                      className="block border border-gray-300 rounded-md px-2 py-1 text-sm w-20" />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[11px] font-bold text-gray-400 uppercase">Users & distributor levers — live terms</p>
        <div className="flex flex-wrap gap-3 items-end">{N("users", "Users")}{N("referred", "% referred")}{N("comm", "Commission %")}
          <label className="text-xs text-gray-600">Window
            <select data-testid="sim-window" value={f.window} onChange={(e) => setF({ ...f, window: e.target.value })} className="block border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-0.5">
              <option value="">Lifetime</option><option value="1">1 month</option><option value="3">3 months</option><option value="6">6 months</option><option value="12">12 months</option>
            </select>
          </label>
        </div>
        <p className="text-[11px] font-bold text-gray-400 uppercase">Monthly costs — live from your Costs tab</p>
        <div className="flex flex-wrap gap-3">{N("fixed", "Fixed $/mo", 1, "w-24")}{N("variable", "Variable $/mo", 1, "w-24")}{N("growth", "Var. growth %")}{N("token_cpu", "Token cost $/user", 0.01, "w-24")}{N("fee_pct", "Payment fee %", 0.1, "w-20")}</div>
        <p className="text-[10px] text-gray-400">Token cost and payment fees scale automatically with users and revenue — simulate 3× users and these costs follow.</p>
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <input data-testid="scenario-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Scenario name" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm flex-1" />
          <button data-testid="scenario-save-btn" onClick={save} disabled={!name.trim()} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-3 py-1.5 disabled:opacity-50">Save</button>
        </div>
        {scenarios.length > 0 && (
          <div className="space-y-1" data-testid="scenario-list">
            {scenarios.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-md px-2.5 py-1.5">
                <span className="font-semibold text-gray-700 flex-1">{s.name}</span>
                <button data-testid={`scenario-load-${s.id}`} onClick={() => loadScenario(s)} className="text-blue-700 font-semibold">Load</button>
                <button data-testid={`scenario-compare-${s.id}`} onClick={() => applyCompare(compare?.name === s.name ? null : s)} className={compare?.name === s.name ? "text-amber-600 font-semibold" : "text-gray-500"}>{compare?.name === s.name ? "Un-compare" : "Compare"}</button>
                <button data-testid={`scenario-del-${s.id}`} onClick={async () => { await api.delete(`/admin/profit/scenarios/${s.id}`); loadScen(); }} className="text-red-500">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-4">
        {live && (
          <div data-testid="sim-live-baseline" className="border border-gray-300 bg-gray-50 rounded-lg px-5 py-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span className="text-[11px] font-bold text-gray-400 uppercase w-full">Live baseline — {monthLabel(live.month)} (real numbers)</span>
            <span>Revenue <b>{usd(live.revenue)}</b></span><span>Costs <b>{usd(live.costs)}</b></span>
            <span className={live.profit >= 0 ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>{live.profit >= 0 ? "🟢 " : "🔴 "}Profit {usd(live.profit)}</span>
          </div>
        )}
        {res && (
          <>
            <div data-testid="sim-result" className={`border rounded-lg p-5 flex flex-wrap gap-x-6 gap-y-1 text-sm ${res.profit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              <span className="text-[11px] font-bold text-gray-400 uppercase w-full">Simulated result</span>
              <span>Revenue <b>{usd(res.revenue)}</b></span><span>Costs <b>{usd(res.costs)}</b></span>
              <span data-testid="sim-commissions">Commissions <b>{usd(res.commission_cost)}</b></span>
              <span data-testid="sim-token-cost">Token cost <b>{usd(res.token_cost)}</b></span>
              <span data-testid="sim-fees">Fees <b>{usd(res.payment_fees)}</b></span>
              <span data-testid="sim-profit" className="font-bold">{res.profit >= 0 ? "🟢 Profit " : "🔴 Loss "}{usd(Math.abs(res.profit))}</span>
              <span data-testid="sim-breakeven" className="text-gray-600">Break-even at <b>{res.break_even_users ?? ">5000"}</b> users</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="sim-users-curve">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">Profit vs users{compare ? ` — vs "${compare.name}"` : ""}</p>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={curveData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="users" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => usd(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line dataKey="profit" name="Profit" stroke="#0071E3" strokeWidth={2} dot={{ r: 3 }} />
                  {compare && <Line dataKey="compare_profit" name={compare.name} stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="sim-comm-curve">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">Profit vs distributor commission % (at {f.users} users)</p>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={res.commission_curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="pct" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => usd(v)} labelFormatter={(v) => `Commission ${v}%`} />
                  <Line dataKey="profit" name="Profit" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DashboardTab = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/profit/dashboard?months=6").then((r) => setD(r.data)).catch((e) => setErr(errDetail(e)));
  }, []);
  if (err) return <p className="text-sm text-red-600 p-6" data-testid="dashboard-error">{err}</p>;
  if (!d) return <p className="text-sm text-gray-400 p-6" data-testid="dashboard-loading">Loading…</p>;
  const c = d.current;
  const profitPos = c.profit >= 0;
  return (
    <div data-testid="dashboard-tab" className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <div data-testid="dash-revenue-card" className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-[11px] font-bold text-gray-400 uppercase">Revenue — {monthLabel(c.month)}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{usd(c.revenue)}</p>
          <p className="text-xs text-gray-500 mt-1">Packages {usd(c.package_revenue)} · Boosters {usd(c.booster_revenue)}</p>
        </div>
        <div data-testid="dash-costs-card" className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-[11px] font-bold text-gray-400 uppercase">Costs</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{usd(c.costs)}</p>
          <p className="text-xs text-gray-500 mt-1">Manual {usd(c.manual_costs)} · Auto {usd(c.auto_costs)}</p>
        </div>
        <div data-testid="dash-profit-card" className={`border rounded-lg p-5 ${profitPos ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
          <p className="text-[11px] font-bold text-gray-400 uppercase">Profit</p>
          <p data-testid="dash-profit-value" className={`text-2xl font-bold mt-1 ${profitPos ? "text-emerald-700" : "text-red-700"}`}>
            {profitPos ? "🟢 +" : "🔴 −"}{usd(Math.abs(c.profit))}{c.margin_pct != null ? ` (${c.margin_pct}%)` : ""}
          </p>
          <p className="text-xs text-gray-500 mt-1">Break-even: {d.break_even_users != null ? `~${d.break_even_users} paying users` : "n/a (no revenue yet)"}</p>
        </div>
      </div>
      <div data-testid="dash-breakdown" className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Where the money goes — {monthLabel(c.month)}</p>
        <div className="space-y-1.5 text-sm max-w-md">
          {[["package-revenue", "Revenue from packages", c.package_revenue, false],
            ["booster-revenue", "Revenue from boosters", c.booster_revenue, false],
            ["manual-costs", "Your added costs (Costs tab)", c.manual_costs, true],
            ["commissions", "Distributor commissions", c.commissions, true],
            ["token-cost", "AI token cost", c.token_cost, true],
            ["payment-fees", "Payment fees", c.payment_fees, true]].map(([id, label, v, neg]) => (
            <div key={id} className="flex justify-between" data-testid={`dash-breakdown-${id}`}>
              <span className="text-gray-600">{label}</span>
              <span className={`font-semibold ${neg ? "text-red-600" : "text-emerald-700"}`}>{neg ? "−" : "+"}{usd(v)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-gray-200 pt-1.5" data-testid="dash-breakdown-profit">
            <span className="font-bold text-gray-900">= Profit</span>
            <span className={`font-bold ${profitPos ? "text-emerald-700" : "text-red-700"}`}>{profitPos ? "+" : "−"}{usd(Math.abs(c.profit))}</span>
          </div>
        </div>
      </div>
      <div data-testid="dash-chart" className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-[11px] font-bold text-gray-400 uppercase mb-3">Revenue vs Costs vs Profit — last 6 months</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={d.months.map((m) => ({ ...m, label: m.month.slice(5) + "/" + m.month.slice(2, 4) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => [usd(v), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="revenue" name="Revenue" fill="#0071E3" radius={[4, 4, 0, 0]} />
            <Bar dataKey="costs" name="Costs" fill="#d1d5db" radius={[4, 4, 0, 0]} />
            <Line dataKey="profit" name="Profit" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div data-testid="dash-unit-economics" className="bg-white border border-gray-200 rounded-lg p-5 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <span className="text-[11px] font-bold text-gray-400 uppercase w-full">Unit economics — {monthLabel(c.month)} (per paying user)</span>
        <span>Paying users: <b data-testid="dash-paying-users">{c.paying_users}</b></span>
        <span>Avg revenue/user: <b>{usd(c.arpu)}</b></span>
        <span>Avg cost/user: <b>{usd(c.acpu)}</b></span>
        <span>Margin/user: <b data-testid="dash-margin-user" className={c.margin_per_user >= 0 ? "text-emerald-700" : "text-red-700"}>{c.margin_per_user >= 0 ? "🟢 " : "🔴 "}{usd(c.margin_per_user)}</b></span>
        <span>Booster attach rate: <b>{c.booster_attach_pct}%</b></span>
      </div>
    </div>
  );
};

const monthShift = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const dt = new Date(y, mo - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CostsTab = () => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ label: "", type: "fixed", amount_usd: "", recurring: false });
  const [editing, setEditing] = useState(null);

  const load = (m) => api.get(`/admin/profit/costs?month=${m}`).then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
  useEffect(() => { load(month); }, [month]);

  const add = async () => {
    try {
      await api.post("/admin/profit/costs", { ...form, amount_usd: parseFloat(form.amount_usd), month });
      setForm({ label: "", type: "fixed", amount_usd: "", recurring: false });
      toast.success("Cost added"); load(month);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/profit/costs/${editing.id}`, { ...editing, amount_usd: parseFloat(editing.amount_usd) });
      setEditing(null); toast.success("Cost updated"); load(month);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/profit/costs/${id}`); toast.success("Cost removed"); load(month); }
    catch (e) { toast.error(errDetail(e)); }
  };

  if (!data) return <p className="text-sm text-gray-400 p-6" data-testid="costs-loading">Loading…</p>;
  return (
    <div data-testid="costs-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="costs-month-prev" onClick={() => setMonth(monthShift(month, -1))} className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm hover:bg-gray-50">◀</button>
          <span data-testid="costs-month-label" className="text-sm font-bold text-gray-900 w-40 text-center">{monthLabel(month)}</span>
          <button data-testid="costs-month-next" onClick={() => setMonth(monthShift(month, 1))} className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm hover:bg-gray-50">▶</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="cost-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Cost item (e.g. Server hosting)" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-52" />
          <select data-testid="cost-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm">
            <option value="fixed">Fixed</option><option value="variable">Variable</option>
          </select>
          <input data-testid="cost-amount-input" type="number" min="0" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
            placeholder="$" className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-24" />
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input data-testid="cost-recurring-checkbox" type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} className="accent-blue-600" />
            Every month
          </label>
          <button data-testid="cost-add-btn" onClick={add} disabled={!form.label.trim() || form.amount_usd === ""}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-50">+ Add cost</button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="costs-table">
          <thead className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase">
            <tr><th className="px-5 py-2">Item</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Recurring</th><th className="px-3 py-2 w-28"></th></tr>
          </thead>
          <tbody>
            {data.items.map((it) => editing?.id === it.id ? (
              <tr key={it.id} className="border-t border-gray-100 bg-blue-50/40" data-testid={`cost-row-edit-${it.id}`}>
                <td className="px-5 py-2"><input data-testid="cost-edit-label" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-sm w-full" /></td>
                <td className="px-3 py-2"><select data-testid="cost-edit-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="border border-gray-300 rounded px-1.5 py-1 text-sm"><option value="fixed">Fixed</option><option value="variable">Variable</option></select></td>
                <td className="px-3 py-2"><input data-testid="cost-edit-amount" type="number" min="0" value={editing.amount_usd} onChange={(e) => setEditing({ ...editing, amount_usd: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-sm w-24" /></td>
                <td className="px-3 py-2"><input type="checkbox" checked={editing.recurring} onChange={(e) => setEditing({ ...editing, recurring: e.target.checked })} className="accent-blue-600" /></td>
                <td className="px-3 py-2 space-x-2"><button data-testid="cost-edit-save" onClick={saveEdit} className="text-xs font-semibold text-blue-700">Save</button><button onClick={() => setEditing(null)} className="text-xs text-gray-400">Cancel</button></td>
              </tr>
            ) : (
              <tr key={it.id} className="border-t border-gray-100" data-testid={`cost-row-${it.id}`}>
                <td className="px-5 py-2.5 font-medium text-gray-900">{it.label}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${it.type === "fixed" ? "bg-gray-100 text-gray-600" : "bg-amber-50 text-amber-700"}`}>{it.type.toUpperCase()}</span></td>
                <td className="px-3 py-2.5">{usd(it.amount_usd)}</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{it.recurring ? "Every month" : "This month"}</td>
                <td className="px-3 py-2.5 space-x-3 text-right">
                  <button data-testid={`cost-edit-${it.id}`} onClick={() => setEditing({ ...it })} className="text-xs font-semibold text-blue-700">Edit</button>
                  <button data-testid={`cost-delete-${it.id}`} onClick={() => del(it.id)} className="text-xs font-semibold text-red-600">Delete</button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-sm text-gray-400" data-testid="costs-empty">No manual costs for this month yet — add your first one above</td></tr>}
            {data.auto.map((a, i) => (
              <tr key={i} className="border-t border-gray-100 bg-blue-50/30" data-testid={`cost-auto-${i}`}>
                <td className="px-5 py-2.5 font-medium text-gray-900">{a.label}</td>
                <td className="px-3 py-2.5"><span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-blue-100 text-blue-700">AUTO 🔗</span></td>
                <td className="px-3 py-2.5">{usd(a.amount_usd)}</td>
                <td className="px-3 py-2.5 text-xs text-gray-400">(live · {a.source})</td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 flex items-center justify-between text-sm">
          <span className="text-gray-500">Manual {usd(data.total_manual)} · Auto {usd(data.total_auto)}</span>
          <span data-testid="costs-total" className="font-bold text-gray-900">TOTAL COSTS — {monthLabel(month).toUpperCase()}: {usd(data.total)}</span>
        </div>
      </div>
    </div>
  );
};

export default function AdminProfit() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("auto");
  const [tab, setTab] = useState("costs");
  const [err, setErr] = useState(null);
  const [advisorPrompt, setAdvisorPrompt] = useState(null);

  useEffect(() => {
    api.get("/admin/profit/models")
      .then((r) => { setData(r.data); setSelected(r.data.selected); })
      .catch((e) => setErr(errDetail(e)));
  }, []);

  const select = async (m) => {
    const prev = selected;
    setSelected(m.id);
    try {
      await api.put("/admin/profit/settings", { advisor_model: m.id });
      toast.success(`Advisor model set to ${m.name}`);
    } catch (e) { setSelected(prev); toast.error(errDetail(e)); }
  };

  return (
    <AdminShell title="Profit Estimation" icon={ChartPieSlice}>
      <div data-testid="profit-page" className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-500">Know your costs, see your profit, simulate your pricing — with an AI advisor on top.</p>
          <ModelPicker data={data} selected={selected} onSelect={select} />
        </div>
        {err && <p data-testid="profit-models-error" className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3">Model catalog unavailable: {err}</p>}
        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} data-testid={`profit-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 text-sm px-4 py-2.5 -mb-px border-b-2 transition-colors ${tab === id ? "border-blue-600 text-blue-700 font-semibold" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {tab === "costs" ? <CostsTab /> : tab === "dashboard" ? <DashboardTab /> : tab === "simulator" ? <SimulatorTab /> : tab === "competitors" ? <CompetitorsTab onAnalyze={(p) => { setAdvisorPrompt(p); setTab("advisor"); }} /> : null}
        <div style={{ display: tab === "advisor" ? "block" : "none" }}>
          <AdvisorTab externalPrompt={advisorPrompt} onConsumed={() => setAdvisorPrompt(null)}
            modelLabel={data?.models.find((m) => m.id === selected)?.name || "Auto (recommended)"} />
        </div>
      </div>
    </AdminShell>
  );
}
